import React, { useId, useState } from 'react';
import './quotationScopeItemInput.css';

export type QuotationScopeItemOption = { text: string; groupLabel: string };

/** Search stays inside the editable row; choosing a library description never changes quote fees. */
export default function QuotationScopeItemInput({ value, onChange, options, label, placeholder, style, multiline = false }: {
  value: string;
  onChange: (value: string) => void;
  options: QuotationScopeItemOption[];
  label: string;
  placeholder: string;
  style?: React.CSSProperties;
  multiline?: boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = options.filter(option => terms.every(term => `${option.text} ${option.groupLabel}`.toLowerCase().includes(term)));
  const matches = filtered.slice(0, 20);
  const choose = (text: string) => { onChange(text); setOpen(false); setActive(-1); };
  const fieldProps = {
    style, value, placeholder, 'aria-label': label, role: 'combobox' as const,
    'aria-autocomplete': 'list' as const, 'aria-expanded': open,
    'aria-controls': `${id}-options`,
    'aria-activedescendant': open && active >= 0 ? `${id}-option-${active}` : undefined,
    onFocus: () => { setQuery(''); setOpen(true); setActive(-1); },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value); setQuery(event.target.value); setOpen(true); setActive(-1);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Escape') { setOpen(false); setActive(-1); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault(); setOpen(true);
        setActive(previous => matches.length ? (event.key === 'ArrowDown'
          ? Math.min(previous + 1, matches.length - 1) : Math.max(previous - 1, 0)) : -1);
      } else if (event.key === 'Enter' && open && (!multiline || active >= 0)) {
        // Searching a row must not accidentally submit/create the surrounding quotation.
        event.preventDefault();
        if (active >= 0 && matches[active]) choose(matches[active].text);
        else if (matches.length === 1) choose(matches[0].text);
        else setActive(matches.length ? 0 : -1);
      }
    },
  };

  return <div className="quotation-scope-search" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    {multiline ? <textarea {...fieldProps} /> : <input {...fieldProps} />}
    {open ? <div className="quotation-scope-search__results">
      <ul id={`${id}-options`} role="listbox" aria-label={`${label} saved items`}>
        {matches.map((option, index) => <li key={`${option.groupLabel}:${option.text}`}>
          <button type="button" role="option" id={`${id}-option-${index}`} aria-selected={active === index}
            onMouseDown={event => event.preventDefault()} onClick={() => choose(option.text)}>
            <span>{option.text}</span><small>{option.groupLabel}</small>
          </button>
        </li>)}
      </ul>
      {!matches.length ? <p>No saved items match. Your wording remains editable.</p> : null}
      {filtered.length > matches.length ? <p>Showing 20 matches. Type more to narrow the list.</p> : null}
    </div> : null}
  </div>;
}
